import type { RouteLocationRaw } from 'vue-router';
import { AREAS, areaDestinations, areaEntity, recordPathFor, viewPathFor, type Area, type AreaId } from '@/navigation/areas';
import { SHARE_KEY_PARAM } from '@/utils/savedViewSharing';
import type { RecentItem } from '@/stores/recentItemsStore';
import { matchRanges } from '@/utils/highlightMatch';

/**
 * Turns Cmd+K's three sources (commands, recent items and the quick-search
 * answer) into the sections the palette draws, for the tab that is open.
 * Kept free of Vue so the rules can be tested on their own.
 */

/** The shape `POST /api/quick_search` answers with (tabasco#825). */
export interface QuickSearchHit {
    id: number;
    display_string: string;
    subtitle: string;
    /** On saved-view hits: the entity whose list the view opens. */
    target_entity?: string;
    /** On shared-view hits: opening one goes through the import page. */
    share_key?: string;
    /** On record hits: the fields the hit matched and the value it matched in (SAU-467). */
    matches?: { field: string; value: string }[];
    /**
     * On players, zones and buckets: the fields the record's icon is drawn from, in the
     * record's own shape, such as `{"player_type": {"id": 2}}`.
     */
    icon?: Record<string, unknown>;
}

export interface QuickSearchResponse {
    groups: { entity: string; hits: QuickSearchHit[] }[];
    unavailable: string[];
}

export interface PaletteCommand {
    id: string;
    label: string;
    to?: RouteLocationRaw;
    /** A named action the palette runs itself, for example opening the bug report. */
    action?: 'report-bug';
    /** The small label on the right of the row, such as the area a list sits in. */
    tag?: string;
    /**
     * Left out of the All tab until something is typed, so the empty palette
     * keeps Recent near the top. The Commands tab always lists it.
     */
    onlyWhenTyped?: boolean;
}

/**
 * "Go to" each area, to wherever `entryOf` says the area opens, then "Go to"
 * each list and page in those areas, tagged with the area's name. Settings
 * gets commands for its pages but none of its own, as it has no icon among
 * the areas. Pass the areas from `visibleAreas`, so a closed gate gives no
 * command.
 */
export function navigationCommands(areas: Area[], entryOf: (area: Area) => RouteLocationRaw): PaletteCommand[] {
    return [
        ...areas
            .filter((area) => area.id !== 'settings')
            .map((area) => ({ id: `area-${area.id}`, label: `Go to ${area.label}`, to: entryOf(area) })),
        ...areas.flatMap((area) =>
            areaDestinations(area).map((destination) => ({
                id: destination.key,
                label: `Go to ${destination.label}`,
                to: destination.path,
                tag: area.label,
                onlyWhenTyped: true,
            })),
        ),
    ];
}

/** The areas whose record types search finds, in the icon strip's order. */
const SEARCHED_AREAS = ['clients', 'players', 'music'] as const satisfies readonly AreaId[];

export type PaletteTabId = 'all' | 'commands' | (typeof SEARCHED_AREAS)[number] | 'views';

/** The record types of one area, in the order its sidebar lists them. */
function areaEntityIds(areaId: AreaId): string[] {
    return AREAS.find((area) => area.id === areaId)?.entities.map((entity) => entity.entityId) ?? [];
}

/** Every record type quick search looks in, in the icon strip's order. */
export const SEARCHED_ENTITY_IDS: readonly string[] = SEARCHED_AREAS.flatMap((areaId) => areaEntityIds(areaId));

/**
 * One tab per area of the app, as the icon strip has, plus All, Commands and
 * Saved views. Joel, 2026-10-17: fifteen record types do not fit a tab each.
 * Inside an area's tab, each record type gets its own heading, such as
 * "Accounts" and "Contacts" under Clients.
 */
export const PALETTE_TABS: readonly { id: PaletteTabId; label: string; entities: string[] }[] = [
    { id: 'all', label: 'All', entities: [] },
    { id: 'commands', label: 'Commands', entities: [] },
    ...SEARCHED_AREAS.map((areaId) => ({
        id: areaId,
        label: AREAS.find((area) => area.id === areaId)?.label ?? areaId,
        entities: areaEntityIds(areaId),
    })),
    { id: 'views', label: 'Saved views', entities: ['SavedView', 'SharedSavedView'] },
];

/**
 * The record types a tab searches, whose tag rows the fields chip opens (SAU-467): every
 * searched type on All, the area's types on an area's tab, and none on Commands or Saved
 * views, which have no fields to choose.
 */
export function tabSearchedEntityIds(tabId: PaletteTabId): string[] {
    if (tabId === 'all') return [...SEARCHED_ENTITY_IDS];
    if (tabId === 'commands' || tabId === 'views') return [];
    return PALETTE_TABS.find((tab) => tab.id === tabId)?.entities ?? [];
}

/**
 * A record type's heading in search: "Accounts", "DDEX tracks", "Players". Built from the
 * noun, not the sidebar label, so players read "Players" here and "All players" in the
 * sidebar.
 */
export function recordTypeHeading(entityId: string): string {
    const noun = areaEntity(entityId)?.noun ?? entityId;
    return `${noun}s`;
}

export const MIN_QUERY_LENGTH = 2;
export const MAX_HITS_PER_TYPE = 5;

/** What a row is, so the palette can draw its icon: a model's own icon, a bookmark, or a prompt. */
export type PaletteItemKind = 'record' | 'view' | 'command';

export interface PaletteItem {
    key: string;
    label: string;
    detail: string;
    kind: PaletteItemKind;
    /** For the user's own saved views: the view's id, so the row can show its icon and colour. */
    viewId?: number;
    /** For records: the model whose icon the row shows. */
    entityId?: string;
    /** Where Enter goes. */
    to?: RouteLocationRaw;
    /** For records: what Cmd+Enter opens in the side peek. */
    peek?: { entityId: string; id: number };
    action?: PaletteCommand['action'];
    /** For records: the fields the hit matched, so the row can highlight them (SAU-467). */
    matches?: { field: string; value: string }[];
    /** For records: what the row's icon is drawn from, so a player shows its type and a zone its brand. */
    iconRow?: Record<string, unknown>;
    /** For players: the id, which the row shows before the name. */
    leadingId?: number;
    /** A short label on the right of the row, such as "Saved view" on a recent view. */
    tag?: string;
}

/** The record types whose rows lead with the record's id, as people name a player by its number. */
const ENTITIES_LED_BY_ID: readonly string[] = ['LocationZonePlayer'];

/** The id a row leads with, or undefined for a record type that does not show it. */
function leadingIdFor(entityId: string, id: number): number | undefined {
    return ENTITIES_LED_BY_ID.includes(entityId) ? id : undefined;
}

export interface PaletteSection {
    label: string;
    items: PaletteItem[];
    /** The one record type a section holds, so the search page can open its list. Absent for commands, recents and views. */
    entityId?: string;
}

export interface PaletteInput {
    query: string;
    tab: PaletteTabId;
    commands: PaletteCommand[];
    recents: RecentItem[];
    response: QuickSearchResponse | null;
    /** The request failed or took longer than two seconds. */
    failed: boolean;
}

export interface PaletteResult {
    /** False below two characters: the palette must not send a search. */
    shouldSearch: boolean;
    sections: PaletteSection[];
    unavailableMessage: string;
}

/**
 * One search hit as a palette row, or null for a saved view that belongs to no
 * list or page, so has no address to open.
 */
function hitItem(entity: string, hit: QuickSearchHit): PaletteItem | null {
    const base = { key: `${entity}:${hit.id}`, label: hit.display_string, detail: hit.subtitle };
    if (entity === 'SharedSavedView' && hit.share_key) {
        return { ...base, kind: 'view', to: { name: 'import-saved-view', query: { [SHARE_KEY_PARAM]: hit.share_key } } };
    }
    if (entity === 'SavedView' && hit.target_entity) {
        const to = viewPathFor(hit.target_entity, hit.id);
        return to ? { ...base, kind: 'view', viewId: hit.id, to } : null;
    }
    return {
        ...base,
        kind: 'record',
        entityId: entity,
        to: recordPathFor(entity, hit.id),
        peek: { entityId: entity, id: hit.id },
        matches: hit.matches ?? [],
        iconRow: hit.icon,
        leadingId: leadingIdFor(entity, hit.id),
    };
}

/** One recent item as a palette row, or null when no area holds its entity or page any more. */
function recentItem(item: RecentItem): PaletteItem | null {
    if (item.kind !== 'view' && !areaEntity(item.entityId)) return null;
    const to = item.kind === 'view' ? viewPathFor(item.entityId, item.id) : recordPathFor(item.entityId, item.id);
    if (!to) return null;
    return {
        key: `recent:${item.kind}:${item.entityId}:${item.id}`,
        label: item.label,
        detail: '',
        tag: item.kind === 'view' ? 'Saved view' : undefined,
        kind: item.kind,
        viewId: item.kind === 'view' ? item.id : undefined,
        entityId: item.entityId,
        to,
        peek: item.kind === 'record' ? { entityId: item.entityId, id: item.id } : undefined,
        leadingId: item.kind === 'record' ? leadingIdFor(item.entityId, item.id) : undefined,
    };
}

/**
 * The matched value a record row also shows (SAU-467), when neither the row's id, its
 * name nor its description shows the typed text, such as a player found by its public IP.
 * Null when the row already shows what matched.
 */
export function paletteSnippet(item: PaletteItem, terms: string[]): string | null {
    const firstMatch = item.matches?.[0];
    if (!firstMatch || terms.length === 0) return null;
    const shownValues = [item.leadingId === undefined ? '' : String(item.leadingId), item.label, item.detail];
    const rowShowsMatch = shownValues.some((value) => matchRanges(value, terms).length > 0);
    return rowShowsMatch ? null : firstMatch.value;
}

/**
 * Whether a command is listed for the typed text. Typed text matches the label
 * only. With nothing typed every command shows, except that the All tab leaves
 * out the ones marked `onlyWhenTyped`, so Recent stays on the first screen.
 */
function commandShows(command: PaletteCommand, lowered: string, tab: PaletteTabId): boolean {
    if (lowered !== '') return command.label.toLowerCase().includes(lowered);
    return !(command.onlyWhenTyped && tab === 'all');
}

export function buildPaletteSections(input: PaletteInput): PaletteResult {
    const query = input.query.trim();
    const shouldSearch = query.length >= MIN_QUERY_LENGTH;
    const lowered = query.toLowerCase();
    const sections: PaletteSection[] = [];

    if (input.tab === 'all' || input.tab === 'commands') {
        const matching = input.commands
            .filter((command) => commandShows(command, lowered, input.tab))
            .map((command): PaletteItem => ({ key: `command:${command.id}`, label: command.label, detail: '', kind: 'command', to: command.to, action: command.action, tag: command.tag }));
        if (matching.length > 0) {
            sections.push({ label: 'Commands', items: matching });
        }
    }

    if (!shouldSearch && input.tab === 'all' && input.recents.length > 0) {
        const recentItems = input.recents.map(recentItem).filter((item): item is PaletteItem => item !== null);
        if (recentItems.length > 0) sections.push({ label: 'Recent', items: recentItems });
    }

    if (shouldSearch && input.response) {
        const hitsOf = (entities: string[]) =>
            input.response!.groups
                .filter((group) => entities.includes(group.entity))
                .flatMap((group) => group.hits.slice(0, MAX_HITS_PER_TYPE).map((hit) => hitItem(group.entity, hit)))
                .filter((item): item is PaletteItem => item !== null);
        for (const tab of PALETTE_TABS) {
            if (tab.entities.length === 0 || (input.tab !== 'all' && input.tab !== tab.id)) {
                continue;
            }
            if (tab.id === 'views') {
                // Your views and shared views read as one list.
                const items = hitsOf(tab.entities);
                if (items.length > 0) sections.push({ label: tab.label, items });
                continue;
            }
            // An area's tab: one heading per record type, in the sidebar's order.
            for (const entityId of tab.entities) {
                const items = hitsOf([entityId]);
                if (items.length > 0) sections.push({ label: recordTypeHeading(entityId), items, entityId });
            }
        }
    }

    return { shouldSearch, sections, unavailableMessage: shouldSearch ? unavailableMessage(input) : '' };
}

/** The saved view groups, which have no area and so no noun of their own. */
const VIEW_GROUP_HEADINGS: Record<string, string> = { SavedView: 'Saved views', SharedSavedView: 'Shared views' };

/**
 * What to say when search failed. The whole request failing says search is unavailable.
 * When the server searched most record types but one failed, the results that came back
 * still show, so the message names only the types that are missing, for example
 * "Albums could not be searched right now". It names only types the open tab searches.
 */
function unavailableMessage(input: PaletteInput): string {
    if (input.failed) return 'Search is unavailable right now';
    const tabEntities = input.tab === 'all' ? null : (PALETTE_TABS.find((tab) => tab.id === input.tab)?.entities ?? []);
    const missing = (input.response?.unavailable ?? [])
        .filter((entity) => tabEntities === null || tabEntities.includes(entity))
        .map((entity) => VIEW_GROUP_HEADINGS[entity] ?? recordTypeHeading(entity));
    if (missing.length === 0) return '';
    return `${missing.join(', ')} could not be searched right now`;
}
